import { WORK_HOURS_PER_DAY } from "./billing-calculations";

function minutesSinceMidnight(value: string): number | null {
  const match = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(value);
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;

  return hours * 60 + minutes;
}

/**
 * Varighet for et deldagsfravær. Tidsrom over midnatt støttes ikke, og
 * kapasitetstrekket begrenses til én intern arbeidsdag (7,5 timer).
 */
export function calculateTimedAbsenceHours(
  startTime: string,
  endTime: string,
): number | null {
  const startMinutes = minutesSinceMidnight(startTime);
  const endMinutes = minutesSinceMidnight(endTime);
  if (startMinutes == null || endMinutes == null || endMinutes <= startMinutes) {
    return null;
  }

  return Math.min((endMinutes - startMinutes) / 60, WORK_HOURS_PER_DAY);
}

export function formatAbsenceTimeRange(
  startTime?: string | null,
  endTime?: string | null,
): string | null {
  if (!startTime || !endTime) return null;
  return `${startTime.slice(0, 5)}–${endTime.slice(0, 5)}`;
}

/**
 * VIB-1731: fornuftsgrense på fraværsdatoer.
 *
 * `<input type="date">` godtar to sifre i årsfeltet og tolker «26» som år 26.
 * Et fravær datert 0026 blir usynlig overalt: ingen månedsvisning treffer
 * det, kapasiteten påvirkes ikke, og det kan ikke slettes fra UI-et fordi
 * ingen liste viser det.
 *
 * Grensen er fast og speiler databaseconstrainten `absences_date_year_bounds`
 * (en CHECK må være immutable, så den kan ikke være relativ til i dag).
 */
export const ABSENCE_MIN_YEAR = 2020;
export const ABSENCE_MAX_YEAR = 2035;

/** `null` når datoen er gyldig, ellers en forklarende melding. */
export function validateAbsenceDate(value: string | null | undefined): string | null {
  if (!value) return null;
  // Splitt på bindestrek, ikke slice(0,4): et femsifret år ("20260-08-04")
  // ville blitt kuttet til "2026" og sluppet gjennom.
  const year = Number(value.split("-")[0]);
  if (!Number.isFinite(year) || year < ABSENCE_MIN_YEAR || year > ABSENCE_MAX_YEAR) {
    return `Årstallet må være mellom ${ABSENCE_MIN_YEAR} og ${ABSENCE_MAX_YEAR}. Skriv hele årstallet, for eksempel 2026.`;
  }
  return null;
}
